import { normalizeExperienceMarks } from '../janus-engine/advancement/experience-marks';
import { createLayerValueReader } from '../janus-engine/compile/layer-value-reader';
type AnyWindow = Record<string, unknown>;

const w = window as unknown as AnyWindow;
w.ftNormalizeExperienceMarks = normalizeExperienceMarks;

function hydrateCampaignLevels(): void {
    const reader = createLayerValueReader({ nodeItemId: () => null, providers: () => [] });
    for (const el of document.querySelectorAll<HTMLElement>('[data-ft-campaign-level]')) {
        el.textContent = String(reader.read(JSON.parse(el.dataset.ftCampaignLevel || '{}'), 'campaign_level'));
    }
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', hydrateCampaignLevels, { once: true });
else hydrateCampaignLevels();

function callGlobal(name: string, ...args: unknown[]): void {
    const fn = w[name];
    if (typeof fn === 'function') {
        fn(...args);
    }
}

function findActionTarget(start: EventTarget | null, selector: string): HTMLElement | null {
    return start instanceof Element ? start.closest<HTMLElement>(selector) : null;
}

function handleSideInfo(el: HTMLElement): void {
    const action = el.dataset.ftSideinfoAction || '';
    if (action === 'previous') callGlobal('goPreviousItemSearches');
    if (action === 'next') callGlobal('goForwardItemSearches');
    if (action === 'item-tab') callGlobal('sideInfoActivateItemTab');
    if (action === 'merchant-tab') callGlobal('sideInfoOnMerchantTabClick');
    if (action === 'trainer-tab') callGlobal('sideInfoOnTrainerTabClick');
    if (action === 'close') callGlobal('closeSideInfoPanel');
    if (action === 'create-monster-template') {
        callGlobal('createMonsterTemplate', el.dataset.ftMonsterTemplateUrl || '');
    }
}

function handleSideLog(el: HTMLElement): void {
    const action = el.dataset.ftSidelogAction || '';
    if (action === 'toggle') {
        callGlobal('toggleEl', el.dataset.ftToggleTarget || '');
        return;
    }
    if (action === 'close') {
        callGlobal('closeSideLog');
        callGlobal('clickEl', 'pills_combat_log_tab');
        return;
    }
    if (action === 'roll-private') {
        const hide = el.dataset.ftHideTarget || '';
        const show = el.dataset.ftShowTarget || '';
        if (hide) callGlobal('hideEl', hide);
        if (show) callGlobal('showEl', show);
        callGlobal('ftSetRollPrivate', Number(el.dataset.ftRollPrivate || '0'));
        return;
    }
    if (action === 'roll-complex') {
        callGlobal('rollComplexDice');
    }
}

function handleQuickRoll(el: HTMLElement): void {
    callGlobal(
        'rollDice',
        'raw',
        '',
        '',
        '',
        '1',
        el.dataset.ftRollDie || '',
        '0',
        el.dataset.ftRollAdvDis || '0',
    );
}

function handleRollDice(el: HTMLElement): void {
    callGlobal(
        'rollDice',
        el.dataset.ftRollType1 || '',
        el.dataset.ftRollType2 || '',
        el.dataset.ftRollItemId || '',
        el.dataset.ftRollDc || '',
        el.dataset.ftRollDice || '',
        el.dataset.ftRollDieValue || '',
        el.dataset.ftRollMod || '0',
        el.dataset.ftRollAdvDis || '0',
    );
}

function handleUseAbility(el: HTMLElement): void {
    const itemId = el.dataset.ftUseAbility || '';
    if (itemId) {
        callGlobal('useAbility', itemId);
    }
}

function handleUseAbilityVariable(el: HTMLElement): void {
    const itemId = el.dataset.ftUseAbilityVariableItem || '';
    const variable = el.dataset.ftUseAbilityVariable || '';
    if (itemId && variable) {
        callGlobal('useAbilityVariable1', itemId, variable);
    }
}

function handlePlayAudio(el: HTMLElement): void {
    const audioId = el.dataset.ftPlayAudioId || '';
    if (!audioId) return;
    callGlobal('openSideLog');
    callGlobal('clickEl', 'pills-soundboard-tab');
    callGlobal('playAudioID', audioId);
}

function handleCopyInput(el: HTMLElement): void {
    const promptId = el.dataset.ftCopyInput || '';
    if (!promptId) return;
    const fields = (el.dataset.ftCopyFields || '')
        .split(',')
        .map((field) => field.trim())
        .filter((field) => field !== '');
    callGlobal('copyInputToClipboard', promptId, ...fields);
}

function handleCopyInputWithItemName(el: HTMLElement): void {
    const promptId = el.dataset.ftCopyInputWithItemName || '';
    if (!promptId) return;
    const fields = (el.dataset.ftCopyFields || '')
        .split(',')
        .map((field) => field.trim())
        .filter((field) => field !== '');
    callGlobal('copyInputToClipboardWithItemName', promptId, ...fields);
}

function handleGenerateObjectEconomics(el: HTMLElement): void {
    const endpoint = el.dataset.ftObjectEconomicsEndpoint || '';
    const field = el.dataset.ftObjectEconomicsField || '';
    if (endpoint && field) {
        callGlobal('generateObjectEconomicsFromGrok', endpoint, el, field);
    }
}

function handleGenerateCarryingCapacity(el: HTMLElement): void {
    const endpoint = el.dataset.ftCarryingCapacityEndpoint || '';
    const field = el.dataset.ftCarryingCapacityField || '';
    if (endpoint && field) {
        callGlobal('generateCarryingCapacityFromGrok', endpoint, el, field);
    }
}

function handleMonsterTemplateAction(el: HTMLElement): void {
    const action = el.dataset.ftMonsterTemplateAction || '';
    if (action === 'import-all-sections') callGlobal('importAllMonsterTemplateSections');
    if (action === 'clear-all-sections') callGlobal('clearAllMonsterTemplateSections');
}

function handleDefaultLocationAudio(el: HTMLElement): void {
    const action = el.dataset.ftDefaultLocationAudioAction || '';
    const prefix = el.dataset.ftDefaultLocationAudioPrefix || '';
    if (!prefix) return;
    if (action === 'clear') {
        callGlobal('setTomSelectValue', `${prefix}_category`, '', { silent: true });
        callGlobal('setTomSelectValue', `${prefix}_subcategory`, '', { silent: true });
        callGlobal('setTomSelectValue', `${prefix}_id`, '', { silent: true });
        document.getElementById(`${prefix}_id`)?.dispatchEvent(new Event('change', { bubbles: true }));
    }
    if (action === 'play') {
        callGlobal('openSideLog');
        callGlobal('clickEl', 'pills-soundboard-tab');
        callGlobal('playAudioFileFromInput1', `${prefix}_id`);
    }
}

function handleToggle(el: HTMLElement): void {
    const target = el.dataset.ftToggleTarget || '';
    if (target) {
        callGlobal('toggleEl', target);
    }
}

function handleToggleSection(el: HTMLElement): void {
    const target = el.dataset.ftToggleSectionTarget || '';
    const header = el.dataset.ftToggleSectionHeader || '';
    const className = el.dataset.ftToggleSectionHeaderClass || '';
    if (target && header) {
        callGlobal('toggleSectionWithHeader', target, header, className);
    }
}

function handleChangeInventoryCharges(el: HTMLElement): void {
    const inventoryId = el.dataset.ftInventoryId || '';
    if (inventoryId && el.id) {
        callGlobal('changeInventoryCharges', inventoryId, el.id);
    }
}

function handleOpenItemInfoValue(el: HTMLElement): void {
    if (el.id) {
        callGlobal('openItemInfoValue', el.id);
    }
}

document.addEventListener('click', (event) => {
    const sideInfo = findActionTarget(event.target, '[data-ft-sideinfo-action]');
    if (sideInfo) {
        event.preventDefault();
        handleSideInfo(sideInfo);
        return;
    }

    const sideLog = findActionTarget(event.target, '[data-ft-sidelog-action]');
    if (sideLog) {
        event.preventDefault();
        handleSideLog(sideLog);
        return;
    }

    const quickRoll = findActionTarget(event.target, '[data-ft-roll-die]');
    if (quickRoll) {
        event.preventDefault();
        handleQuickRoll(quickRoll);
        return;
    }

    const rollDice = findActionTarget(event.target, '[data-ft-roll-type1]');
    if (rollDice) {
        event.preventDefault();
        handleRollDice(rollDice);
        return;
    }

    const useAbilityVariable = findActionTarget(event.target, '[data-ft-use-ability-variable-item]');
    if (useAbilityVariable) {
        event.preventDefault();
        handleUseAbilityVariable(useAbilityVariable);
        return;
    }

    const useAbility = findActionTarget(event.target, '[data-ft-use-ability]');
    if (useAbility) {
        event.preventDefault();
        handleUseAbility(useAbility);
        return;
    }

    const playAudio = findActionTarget(event.target, '[data-ft-play-audio-id]');
    if (playAudio) {
        event.preventDefault();
        handlePlayAudio(playAudio);
        return;
    }

    const copyInputWithItemName = findActionTarget(event.target, '[data-ft-copy-input-with-item-name]');
    if (copyInputWithItemName) {
        event.preventDefault();
        handleCopyInputWithItemName(copyInputWithItemName);
        return;
    }

    const copyInput = findActionTarget(event.target, '[data-ft-copy-input]');
    if (copyInput) {
        event.preventDefault();
        handleCopyInput(copyInput);
        return;
    }

    const objectEconomics = findActionTarget(event.target, '[data-ft-object-economics-endpoint]');
    if (objectEconomics) {
        event.preventDefault();
        handleGenerateObjectEconomics(objectEconomics);
        return;
    }

    const carryingCapacity = findActionTarget(event.target, '[data-ft-carrying-capacity-endpoint]');
    if (carryingCapacity) {
        event.preventDefault();
        handleGenerateCarryingCapacity(carryingCapacity);
        return;
    }

    const monsterTemplateAction = findActionTarget(event.target, '[data-ft-monster-template-action]');
    if (monsterTemplateAction) {
        event.preventDefault();
        handleMonsterTemplateAction(monsterTemplateAction);
        return;
    }

    const defaultLocationAudio = findActionTarget(event.target, '[data-ft-default-location-audio-action]');
    if (defaultLocationAudio) {
        event.preventDefault();
        handleDefaultLocationAudio(defaultLocationAudio);
        return;
    }

    const openInfo = findActionTarget(event.target, '[data-ft-open-item-info]');
    if (openInfo) {
        event.preventDefault();
        callGlobal('openItemInfo', openInfo.dataset.ftOpenItemInfo || '');
        return;
    }

    const toggleSection = findActionTarget(event.target, '[data-ft-toggle-section-target]');
    if (toggleSection) {
        event.preventDefault();
        handleToggleSection(toggleSection);
        return;
    }

    const toggle = findActionTarget(event.target, '[data-ft-toggle-target]');
    if (toggle) {
        event.preventDefault();
        handleToggle(toggle);
    }
});

document.addEventListener('change', (event) => {
    const audioCategory = findActionTarget(event.target, '[data-ft-audio-category-prefix]');
    if (audioCategory) {
        const prefix = audioCategory.dataset.ftAudioCategoryPrefix || '';
        if (prefix) {
            callGlobal(
                'queryData2Input1',
                `${w.BASE_URL || ''}options/forms/select_query_audio_subcategories_inc.php`,
                `div_refresh_audio_subcategory_${prefix}`,
                'amb',
                prefix,
                `${prefix}_category`,
            );
        }
        return;
    }

    const audioSubcategory = findActionTarget(event.target, '[data-ft-audio-subcategory-prefix]');
    if (audioSubcategory) {
        const prefix = audioSubcategory.dataset.ftAudioSubcategoryPrefix || '';
        if (prefix) {
            callGlobal(
                'queryData2Input2',
                `${w.BASE_URL || ''}options/forms/select_query_audio_names_inc.php`,
                `div_refresh_audio_names_${prefix}`,
                'amb',
                prefix,
                `${prefix}_category`,
                `${prefix}_subcategory`,
            );
        }
        return;
    }

    const audioName = findActionTarget(event.target, '[data-ft-audio-name-input]');
    if (audioName) {
        const id = (audioName as HTMLSelectElement).id || audioName.dataset.ftAudioNameInput || '';
        if (id) callGlobal('onEncounterAmbienceAudioSelected', id);
        return;
    }

    const inventoryLayerFilter = findActionTarget(event.target, '[data-ft-inventory-layer-item-type-filter]');
    if (inventoryLayerFilter) {
        callGlobal('ftInventoryLayerItemTypeFilterChanged', inventoryLayerFilter);
        return;
    }

    const inventoryCharges = findActionTarget(event.target, '[data-ft-change-inventory-charges]');
    if (inventoryCharges) {
        handleChangeInventoryCharges(inventoryCharges);
        return;
    }

    const openInfoValue = findActionTarget(event.target, '[data-ft-open-item-info-value]');
    if (openInfoValue) {
        handleOpenItemInfoValue(openInfoValue);
    }
});
